import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Plus, ChevronDown, ChevronRight, Star } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useRevisionTemplates } from "@/hooks/useRevisionTemplates";

interface RevisionTemplatesProps {
  onApplyTemplate: (template: string) => void;
  isLoading?: boolean;
}

interface Template {
  id: string;
  label: string;
  prompt: string;
  category: 'general' | 'mrcp' | 'sce' | 'gp' | 'licensing' | 'custom';
  subcategory?: string;
  isCustom?: boolean;
  isFavorite?: boolean;
}

const predefinedTemplates: Template[] = [
  // General Templates
  { id: 'student', label: 'Medical Students', prompt: 'Make this suitable for medical students with clear explanations', category: 'general' },
  { id: 'clinical', label: 'Clinical Guidelines', prompt: 'Convert to clinical guidelines format with evidence-based recommendations', category: 'general' },
  { id: 'patient', label: 'Patient Education', prompt: 'Simplify for patient education with accessible language', category: 'general' },
  { id: 'exam', label: 'General Exam Prep', prompt: 'Format for exam preparation with key points and mnemonics', category: 'general' },

  // MRCP Pathway
  { id: 'mrcp_part1', label: 'MRCP Part 1', prompt: 'Format for MRCP Part 1 exam with emphasis on: basic sciences, clinical pharmacology, statistics, physiology, anatomy, and MCQ-style learning points. Include best-of-five question practice points.', category: 'mrcp' },
  { id: 'mrcp_part2', label: 'MRCP Part 2', prompt: 'Structure for MRCP Part 2 Written exam focusing on: clinical sciences, evidence-based diagnosis and management, case scenarios, differential diagnoses, investigation interpretation, and treatment guidelines.', category: 'mrcp' },
  { id: 'mrcp_paces', label: 'MRCP PACES', prompt: 'Format for MRCP PACES exam preparation with focus on: clinical examination skills, history-taking, communication, examination findings, differential diagnoses, and station-specific approaches (respiratory, cardiovascular, abdominal, neurological, communication skills).', category: 'mrcp' },

  // Specialty Certificate Examinations (SCE)
  { id: 'sce_diabetes_endo', label: 'SCE Diabetes & Endocrinology', prompt: 'Format this for the SCE in Diabetes and Endocrinology exam. Include: advanced endocrine pathophysiology, complex diabetes management scenarios, thyroid disorders, pituitary and adrenal conditions, bone and calcium disorders, evidence-based management guidelines, NICE/BNF references, interpretation of endocrine function tests, and clinical case-based learning points suitable for specialty registrars.', category: 'sce', subcategory: 'endocrinology' },
  { id: 'sce_gastro', label: 'SCE Gastroenterology', prompt: 'Format for SCE in Gastroenterology with focus on: advanced GI pathology, endoscopy findings, inflammatory bowel disease management, liver disease, pancreatic disorders, nutrition, and specialty-level clinical scenarios.', category: 'sce', subcategory: 'gastroenterology' },
  { id: 'sce_cardio', label: 'SCE Cardiology', prompt: 'Structure for SCE in Cardiology covering: advanced arrhythmia management, heart failure, interventional cardiology, imaging interpretation (echo, angiography), and complex cardiovascular scenarios.', category: 'sce', subcategory: 'cardiology' },
  { id: 'sce_respiratory', label: 'SCE Respiratory Medicine', prompt: 'Format for SCE in Respiratory Medicine with emphasis on: advanced lung disease management, imaging interpretation, respiratory physiology, sleep medicine, and specialty-level case scenarios.', category: 'sce', subcategory: 'respiratory' },

  // GP Pathway
  { id: 'mrcgp_akt', label: 'MRCGP AKT', prompt: 'Format for MRCGP Applied Knowledge Test focusing on: primary care clinical knowledge, evidence-based practice, statistics and epidemiology, administrative knowledge, and practice-based scenarios.', category: 'gp' },
  { id: 'mrcgp_csa', label: 'MRCGP CSA', prompt: 'Structure for MRCGP Clinical Skills Assessment with focus on: consultation skills, data gathering, clinical management, interpersonal skills, patient-centered approach, safety netting, and 10-minute consultation scenarios.', category: 'gp' },
  { id: 'mrcgp_general', label: 'MRCGP General', prompt: 'Adapt this for MRCGP exam preparation. Focus on: primary care context, holistic patient-centered approach, NICE guidelines, red flags, safety netting, consultation skills, practice management aspects, and community-based care.', category: 'gp' },

  // Licensing Exams
  { id: 'plab1', label: 'PLAB Part 1', prompt: 'Format for PLAB Part 1 exam focusing on: UK clinical practice guidelines, GMC Good Medical Practice, clinical sciences, MCQ technique, and common UK hospital presentations.', category: 'licensing' },
  { id: 'plab2', label: 'PLAB Part 2', prompt: 'Structure for PLAB Part 2 OSCE exam with emphasis on: clinical examination skills in UK context, communication with patients and colleagues, UK emergency protocols, practical procedures, and cultural competence in NHS setting.', category: 'licensing' },
];

const categoryLabels = {
  general: { label: '📚 General', icon: FileText },
  mrcp: { label: '🎓 MRCP Pathway', icon: FileText },
  sce: { label: '🔬 Specialty Certificate Exams (SCE)', icon: FileText },
  gp: { label: '🏥 GP Pathway (MRCGP)', icon: FileText },
  licensing: { label: '📋 Licensing Exams', icon: FileText },
  custom: { label: '✨ My Custom Templates', icon: FileText },
};

export const RevisionTemplates = ({ onApplyTemplate, isLoading }: RevisionTemplatesProps) => {
  const { customTemplates, toggleFavorite, deleteTemplate } = useRevisionTemplates();
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({
    general: true,
    mrcp: false,
    sce: true,
    gp: false,
    licensing: false,
    custom: false,
  });

  // Combine predefined and custom templates
  const allTemplates = [
    ...predefinedTemplates,
    ...customTemplates.map(t => ({
      id: t.id,
      label: t.name,
      prompt: t.prompt,
      category: (t.category || 'custom') as Template['category'],
      subcategory: t.subcategory,
      isCustom: true,
      isFavorite: t.is_favorite,
    })),
  ];

  // Get favorites
  const favorites = allTemplates.filter(t => t.isFavorite);

  // Group templates by category
  const templatesByCategory = allTemplates.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {} as Record<string, Template[]>);

  const toggleCategory = (category: string) => {
    setOpenCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Revision Templates
          </div>
          <Button variant="ghost" size="sm" className="h-8 gap-1">
            <Plus className="h-4 w-4" />
            New
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Favorites Section */}
        {favorites.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              Favorites
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {favorites.map(template => (
                <Button
                  key={template.id}
                  variant="outline"
                  size="sm"
                  onClick={() => onApplyTemplate(template.prompt)}
                  disabled={isLoading}
                  className="justify-start"
                >
                  {template.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Category Sections */}
        {Object.entries(categoryLabels).map(([categoryKey, { label }]) => {
          const templates = templatesByCategory[categoryKey] || [];
          if (templates.length === 0) return null;

          return (
            <Collapsible
              key={categoryKey}
              open={openCategories[categoryKey]}
              onOpenChange={() => toggleCategory(categoryKey)}
            >
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start font-medium h-8"
                >
                  {openCategories[categoryKey] ? (
                    <ChevronDown className="h-4 w-4 mr-2" />
                  ) : (
                    <ChevronRight className="h-4 w-4 mr-2" />
                  )}
                  {label}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {templates.length}
                  </span>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <div className="grid grid-cols-2 gap-2">
                  {templates.map(template => (
                    <Button
                      key={template.id}
                      variant="outline"
                      size="sm"
                      onClick={() => onApplyTemplate(template.prompt)}
                      disabled={isLoading}
                      className="justify-start"
                    >
                      {template.label}
                    </Button>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </CardContent>
    </Card>
  );
};
